import { DocumentSnapshot, onSnapshot, Unsubscribe, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import {
  Paths,
  Page,
  assertCurrentUid,
  debounceManager,
  debouncedSetDoc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  ts,
  updateDoc,
  withBackoff,
} from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import { ChatThread } from '../components/GlobalChatSystem/types';
import { ChatThreadDoc, fromChatThreadDoc, toChatThreadDoc } from '../types/chatThreadIndexFirestore';
import { getActiveWorkspaceAccount, onActiveWorkspaceResolved } from '../services/activeWorkspace';
import { readConfirmedWorkspace } from '../services/prest0nRuntime/confirmedWorkspaceCache';
import { readArchivedAtMs } from '../services/groupThreads/threadClosure';
import { parseArchiveRequestStamps, type ThreadArchiveRequest } from '../services/groupThreads/threadArchiveRequest';
import { THREAD_PAGE_SIZE, WORKING_SET_LIMIT, type ThreadWindowGrowth } from './chatThreadWindow';

/** Exported for chatThreadLanes, which builds its queries on the same collection. */
export const THREAD_DOMAIN = 'chat_threads';
const META_DOMAIN = 'chat_threads_meta';
const ACTIVE_DOC_ID = 'active';
// The window constants live in a Firebase-free leaf (chatThreadWindow) so a
// jest.mock of THIS module cannot erase them — see that file's header.
// Re-exported here so existing import sites keep one place to look.
export { WORKING_SET_LIMIT, THREAD_PAGE_SIZE } from './chatThreadWindow';
const DEFAULT_PAGE_SIZE = THREAD_PAGE_SIZE;
const WRITE_DEBOUNCE_MS = 150;
const PREST0N_CF_BASE_URL = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';

const pendingWritesByUid: Map<string, Promise<void>> = new Map();

function enqueueUserWrite(uid: string, work: () => Promise<void>): Promise<void> {
  const previous = pendingWritesByUid.get(uid) || Promise.resolve();
  const next = previous.catch(() => {}).then(work);
  pendingWritesByUid.set(uid, next);
  next.finally(() => {
    if (pendingWritesByUid.get(uid) === next) {
      pendingWritesByUid.delete(uid);
    }
  }).catch(() => {});
  return next;
}

function threadRef(uid: string, threadId: string) {
  return Paths.domainDoc(uid, THREAD_DOMAIN, threadId);
}

// ── INCIDENT-20260818: deferred FULL-SHAPE creates (bind-at-birth) ──────────
// A thread create that races the workspace resolution used to go out UNBOUND
// (and a narrow two-field re-stamp could mint a PARTIAL, index-invisible doc
// when it raced the debounced create — M12). Creates now PARK the full
// payload until the workspace resolves; updates refresh the parked snapshot;
// the resolution listener flushes full create-shaped writes through the same
// per-uid queue + debounce discipline. A thread is therefore never written
// unbound and never written partial.
// `intendedAccountId` (2026-08-20): the workspace the thread was CREATED IN,
// captured at park time from the confirmed-workspace cache (the same source
// the boot guard trusts — written only on successful strict resolution). The
// flush used to bind every parked entry to whatever accountId resolved
// LATEST — a rapid workspace switch between park and flush bound org A's
// draft into org B, arming the live containment effect to yank it later.
const deferredCreates = new Map<string, {
  uid: string;
  instanceId: string;
  thread: ChatThread;
  intendedAccountId: string | null;
}>();

/**
 * Backstop-only binding stamp for docs that ALREADY EXIST unbound (legacy
 * stock the sweep has not reached). Existence-probed so it can never mint a
 * partial two-field doc (M12). Not reachable from the create path anymore.
 */
async function stampThreadWorkspaceBinding(
  uid: string,
  threadId: string,
  accountId: string,
): Promise<void> {
  addUserScopedWriteGuard(uid, 'stampThreadWorkspaceBinding');
  assertCurrentUid(uid, 'stampThreadWorkspaceBinding');
  const ref = threadRef(uid, threadId);
  const snap = await withBackoff(() => getDoc(ref));
  if (!snap.exists()) return; // never create — the deferred-create path owns creates
  await enqueueUserWrite(uid, async () => {
    await withBackoff(() => setDoc(ref, {
      accountId,
      sessionId: `sess_thr_${threadId}`,
    }, { merge: true }));
  });
}
export { stampThreadWorkspaceBinding };

/**
 * ONE WRITER FOR A THREAD'S SECTION (2026-09-29). A filing is its own small
 * write, handed to Firestore at once. It never takes a place in the per-person
 * queue above, where each save waits for the server to confirm the one before
 * it, and where anything still waiting is lost on a reload.
 *
 * Read from the live check that failed its first attempt for days
 * (`category-file-and-rename`, run 36476599680): at the end of a reply the
 * server writes the same thread record, the queue held the filing behind
 * repeated whole-record re-saves, and the filing never reached the server.
 * Firestore keeps its own pending writes on disk and applies them to every copy
 * it hands back at once, so a filing written here shows everywhere immediately
 * and survives a reload.
 *
 * `updateDoc`, never a merge set: a record that does not exist yet is refused
 * (`not-found`) instead of being created without `archived` or its workspace
 * (M12). The caller falls back to the ordinary save for that case. Whole-record
 * saves no longer carry `category` (toChatThreadDoc), so on an existing record
 * this is the only client writer of a filing, and a stale copy in another tab
 * cannot undo it.
 */
export async function writeChatThreadCategory(
  uid: string,
  threadId: string,
  category: string,
  categoryAt?: number,
): Promise<void> {
  addUserScopedWriteGuard(uid, 'writeChatThreadCategory');
  assertCurrentUid(uid, 'writeChatThreadCategory');
  const stamp = typeof categoryAt === 'number' && Number.isFinite(categoryAt) ? { categoryAt } : {};
  await withBackoff(() => updateDoc(threadRef(uid, threadId), { category, updatedAtTs: ts.serverNow(), ...stamp }));
}

/**
 * CONVERGED UNREAD (PR-3, 2026-09-08): the viewer dwelt on a shared thread —
 * zero the server-owned `unseenCount` on THEIR index doc. `updateDoc` never
 * creates (a missing doc is a no-op, never an index-invisible partial — M12)
 * and never touches another field, so the whole-record upsert's stale-clobber
 * gates stay intact. firestore.rules admits exactly this write (0) and refuses
 * any client value above it. Resolves true when the server took the write.
 */
export async function clearThreadUnseen(uid: string, threadId: string): Promise<boolean> {
  const id = (threadId ?? '').trim();
  if (!uid || !id) return false;
  try {
    addUserScopedWriteGuard(uid, 'clearThreadUnseen');
    assertCurrentUid(uid, 'clearThreadUnseen');
  } catch {
    return false;
  }
  const ref = threadRef(uid, id);
  try {
    await enqueueUserWrite(uid, async () => {
      await withBackoff(() => updateDoc(ref, { unseenCount: 0 }));
    });
    return true;
  } catch (error) {
    const code = (error as { code?: string } | null)?.code ?? '';
    if (code !== 'not-found') {
      console.warn('[chatThreadIndexRepository] clearThreadUnseen failed (non-fatal)', {
        threadId: id,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    return false;
  }
}

/** Flush parked creates as FULL create-shaped writes (resolution landed). */
function flushDeferredCreates(accountId: string): void {
  if (deferredCreates.size === 0) return;
  const pending = Array.from(deferredCreates.values());
  deferredCreates.clear();
  for (const entry of pending) {
    try {
      addUserScopedWriteGuard(entry.uid, 'flushDeferredCreates');
      assertCurrentUid(entry.uid, 'flushDeferredCreates');
    } catch {
      continue; // uid changed since parking (sign-out) — sweep is the net
    }
    const ref = threadRef(entry.uid, entry.thread.id);
    const data = toChatThreadDoc(entry.thread, entry.instanceId, {
      isCreate: true,
      // Park-time intent wins over whatever workspace happened to resolve
      // latest; the resolved id remains the fallback for entries parked
      // before any confirmed workspace existed.
      accountId: entry.intendedAccountId || accountId,
    });
    void enqueueUserWrite(entry.uid, async () => {
      await withBackoff(() => debouncedSetDoc(ref, data, {
        debounceKey: ref.path,
        debounceMs: WRITE_DEBOUNCE_MS,
      }));
    }).catch((err) => {
      console.warn('[chatThreadIndexRepository] deferred-create flush failed (non-fatal)', {
        threadId: entry.thread.id,
        error: err instanceof Error ? err.message : String(err),
      });
    });
  }
}

// Module-scope registration: the chat mount publishes the resolved workspace
// (services/activeWorkspace.ts) — parked creates flush the moment it lands.
onActiveWorkspaceResolved((accountId) => flushDeferredCreates(accountId));

// Best-effort last-chance flush on page teardown: a parked create whose
// resolution never landed this page load is written UNBOUND rather than not
// at all (the corrected M4 sweep binds it within the hour; losing the doc
// entirely is the strictly worse outcome — M1's disappeared-thread class).
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (deferredCreates.size === 0) return;
    const pending = Array.from(deferredCreates.values());
    deferredCreates.clear();
    for (const entry of pending) {
      try {
        const ref = threadRef(entry.uid, entry.thread.id);
        // Park-time intent beats the old hard-null: an org-created draft
        // flushed at pagehide used to go out UNBOUND and the M4 sweep then
        // bound it to the member's canonical org — a guaranteed foreign
        // flip for multi-workspace members. No intent → unbound as before.
        const data = toChatThreadDoc(entry.thread, entry.instanceId, {
          isCreate: true,
          accountId: entry.intendedAccountId,
        });
        void setDoc(ref, data, { merge: true });
      } catch { /* best-effort */ }
    }
  });
}

function activeRef(uid: string) {
  return Paths.domainDoc(uid, META_DOMAIN, ACTIVE_DOC_ID);
}

function clearThreadSessionStorage(threadId: string): void {
  if (typeof localStorage === 'undefined') return;
  const suffix = `_${threadId}`;
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (!key.startsWith('prest0_session_')) continue;
    if (!key.endsWith(suffix)) continue;
    localStorage.removeItem(key);
  }
}

async function archiveThreadSessionOnBackend(threadId: string): Promise<void> {
  const user = getAuth().currentUser;
  if (!user) return;
  const token = await user.getIdToken(false);
  const response = await fetch(`${PREST0N_CF_BASE_URL}/prest0nSetSessionStatus`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ threadId, status: 'archived' }),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`prest0nSetSessionStatus failed: ${response.status} ${body}`);
  }
}

/**
 * P5 stage 1 (one thread universe, dual-READ): the main web surface reads BOTH
 * its canonical 'site_chat' bucket AND the legacy 'global_chat' bucket (the
 * pre-refactor default instanceId), so threads created under the old id become
 * visible again instead of being permanently stranded. Write-canonicalization
 * (re-stamping legacy docs) is stage 2, after this read soaks in prod —
 * mirroring the per-message-doc migration's staging. Internal surfaces
 * (citadel_chat, etc.) stay partitioned.
 */
/**
 * Exported since 2026-09-21: the bell asks the durable tombstone map about a
 * thread of the MAIN chat surface, and the map is namespaced by instance id.
 * A second spelling out there would read an empty map forever — silently, and
 * in the safe-looking direction — so the constant is shared rather than
 * respelled.
 */
export const MAIN_SURFACE_INSTANCE_ID = 'site_chat';
export const LEGACY_MAIN_INSTANCE_IDS = ['site_chat', 'global_chat'];

/**
 * What a live window's snapshot was, beside its rows (2026-09-25).
 *
 * `limit` is the limit the DELIVERING query ran with — the only honest input to
 * the store's `windowCoverageOf`, now that the workspace window can grow: judging
 * a 140-row window against 40 would read it as saturated with a floor 100 rows too
 * new, and every thread in between would stop being droppable when it should be.
 * `lastDoc` is the window's oldest row, as a cursor: "load older threads" starts
 * after it instead of re-reading the window from its top.
 */
export interface ThreadWindowMeta {
  limit: number;
  lastDoc: DocumentSnapshot | null;
}

export function subscribeChatThreadIndex(
  uid: string,
  instanceId: string,
  cb: (threads: ChatThread[], meta: ThreadWindowMeta) => void,
): Unsubscribe {
  assertCurrentUid(uid, 'subscribeChatThreadIndex');
  const colRef = Paths.domainCol(uid, THREAD_DOMAIN);
  const instanceFilter = instanceId === MAIN_SURFACE_INSTANCE_ID
    ? where('instanceId', 'in', LEGACY_MAIN_INSTANCE_IDS)
    : where('instanceId', '==', instanceId);
  const threadQuery = query(
    colRef,
    instanceFilter,
    where('archived', '==', false),
    orderBy('lastActiveAt', 'desc'),
    limit(WORKING_SET_LIMIT),
  );

  // M11 (INCIDENT-20260818): this listener previously had NO error callback —
  // a missing composite index, a rules denial, or a dropped stream failed
  // SILENTLY: no server threads ever arrived and nothing was logged. Errors
  // now log loudly and resubscribe on capped backoff; the local list is
  // untouched during the outage (reconcile only runs on snapshots).
  let disposed = false;
  let inner: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryDelayMs = 2000;
  const attach = () => {
    if (disposed) return;
    inner = onSnapshot(threadQuery, (snapshot) => {
      retryDelayMs = 2000; // healthy snapshot resets the backoff
      const threads = snapshot.docs
        .map((docSnap) => docSnap.data() as ChatThreadDoc)
        .map(fromChatThreadDoc);
      const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      cb(threads, { limit: WORKING_SET_LIMIT, lastDoc });
    }, (err) => {
      console.error('[chatThreadIndexRepository] thread index subscription errored — resubscribing', {
        uid,
        instanceId,
        delayMs: retryDelayMs,
        error: err instanceof Error ? err.message : String(err),
      });
      try { inner?.(); } catch { /* already torn down */ }
      inner = null;
      if (disposed) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        attach();
      }, retryDelayMs);
      retryDelayMs = Math.min(60_000, Math.round(retryDelayMs * 2));
    });
  };
  attach();
  return () => {
    disposed = true;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    try { inner?.(); } catch { /* ignore */ }
    inner = null;
  };
}

/**
 * Per-WORKSPACE listen window (multi-org separation round 2, 2026-08-19).
 * The main subscription above is workspace-blind — the newest
 * WORKING_SET_LIMIT threads across EVERY organization — so a busy org's
 * activity pushed a quieter org's threads out of the window entirely and its
 * rail collapsed ("threads keep changing"). This companion subscription
 * scopes the SAME query to one workspace binding, guaranteeing the active
 * org its own full window; the store merges both streams by id. Uses the
 * (instanceId, accountId, archived, lastActiveAt) composite index that
 * shipped with the M4 backfill.
 *
 * `growth` (2026-09-25, chatThreadWindowGrowth.workspaceWindowGrowth): the store's
 * OWN workspace window grows while triage-report rows crowd the person's threads
 * out of it, and remembers the size it reached on this device. Growing re-attaches
 * the listener at the larger limit; the rows the smaller window delivered stand
 * until the larger one answers, and every delivery names the limit its query ran
 * with. The rule is passed in, never imported: this module rides the first
 * download (the notifications bell reads it) and the rule is the chat's alone. The
 * other-workspace windows (display only) pass none and never grow.
 */
export function subscribeChatThreadIndexForWorkspace(
  uid: string,
  instanceId: string,
  accountId: string,
  cb: (threads: ChatThread[], meta: ThreadWindowMeta) => void,
  options?: { growth?: ThreadWindowGrowth },
): Unsubscribe {
  assertCurrentUid(uid, 'subscribeChatThreadIndexForWorkspace');
  const colRef = Paths.domainCol(uid, THREAD_DOMAIN);
  const instanceFilter = instanceId === MAIN_SURFACE_INSTANCE_ID
    ? where('instanceId', 'in', LEGACY_MAIN_INSTANCE_IDS)
    : where('instanceId', '==', instanceId);
  const growth = options?.growth ?? null;
  const isSize = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= WORKING_SET_LIMIT;
  let windowLimit = growth && isSize(growth.startLimit) ? growth.startLimit : WORKING_SET_LIMIT;
  const windowQuery = (size: number) => query(
    colRef,
    instanceFilter,
    where('accountId', '==', accountId),
    where('archived', '==', false),
    orderBy('lastActiveAt', 'desc'),
    limit(size),
  );

  // Error+backoff parity with the global listener (owner report 2026-08-19
  // "threads are disappearing"): this companion stream previously had NO
  // error callback — an index problem or rules denial silenced the ACTIVE
  // workspace's own window, so a global-only (cross-org top-40) snapshot
  // drove the reconcile and the quiet org's threads vanished. Same loud
  // log + capped-backoff resubscribe as the global stream; the merge layer
  // additionally holds reconcile drops until both streams deliver.
  let disposed = false;
  let inner: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryDelayMs = 2000;
  // Which attach is current. A grown window replaces the listener, and the
  // replaced one can still have a snapshot or an error in flight: neither may
  // speak for the window any more.
  let attachSeq = 0;
  const grow = (seq: number, next: number) => {
    if (disposed || seq !== attachSeq || next <= windowLimit) return;
    windowLimit = next;
    growth?.remember(next);
    // A retry waiting on an error would attach a second listener over this one.
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    const previous = inner;
    inner = null;
    try { previous?.(); } catch { /* already torn down */ }
    attach();
  };
  const attach = () => {
    if (disposed) return;
    const attachedLimit = windowLimit;
    attachSeq += 1;
    const seq = attachSeq;
    inner = onSnapshot(windowQuery(attachedLimit), (snapshot) => {
      if (seq !== attachSeq) return;
      retryDelayMs = 2000; // healthy snapshot resets the backoff
      const threads = snapshot.docs
        .map((docSnap) => docSnap.data() as ChatThreadDoc)
        .map(fromChatThreadDoc);
      const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      cb(threads, { limit: attachedLimit, lastDoc });
      if (!growth || disposed) return;
      const next = growth.next(threads, attachedLimit);
      // After the current call stack: the listener handle is assigned only once
      // onSnapshot returns, and a snapshot can be delivered before that.
      if (isSize(next) && next > attachedLimit) void Promise.resolve().then(() => grow(seq, next));
    }, (err) => {
      if (seq !== attachSeq) return;
      console.error('[chatThreadIndexRepository] workspace thread index subscription errored — resubscribing', {
        uid,
        instanceId,
        accountId,
        delayMs: retryDelayMs,
        error: err instanceof Error ? err.message : String(err),
      });
      try { inner?.(); } catch { /* already torn down */ }
      inner = null;
      if (disposed) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        attach();
      }, retryDelayMs);
      retryDelayMs = Math.min(60_000, Math.round(retryDelayMs * 2));
    });
  };
  attach();
  return () => {
    disposed = true;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    try { inner?.(); } catch { /* ignore */ }
    inner = null;
  };
}

/**
 * Most archived threads one pass will hold facts for.
 *
 * Bounded on purpose. A real account in this repo's own archive test carried
 * 131 archived threads of 223 (useChatThreads.archivedThreadLeavesTheRail),
 * so an unbounded listener here would grow for the life of an account to
 * answer a question about the handful of threads a mention list names. The
 * probe in threadClosure covers anything older than the window.
 */
export const ARCHIVED_CLOSURE_WINDOW = 200;

/**
 * The viewer's own ARCHIVED threads, so a surface that remembers a thread after
 * it closes — the mentions tab, the bell — can say so (2026-09-21 owner
 * directive). The exact inverse of the workspace listener above: same
 * collection, same composite index, `archived == true`.
 *
 * ORDERED BY `lastActiveAt`, NEVER `archivedAt`. Firestore drops a document
 * that lacks the ordered field, and only ONE of the four archive writers stamps
 * `archivedAt` — the membership fanout. `archiveChatThread` writes
 * `{archived, instanceId}` and threadJanitor writes `archivedAtTs`, so ordering
 * on `archivedAt` would silently omit every personally-archived and every
 * janitor-archived thread, in a query that would have looked right. This is the
 * renewalNoticeSweep class (CLAUDE.md): `lastActiveAt` is a required field of
 * ChatThreadDoc, so it drops nothing.
 *
 * It rides the (instanceId, accountId, archived, lastActiveAt) composite that
 * `subscribeChatThreadIndexForWorkspace` already queries in production — an
 * equality flip does not change index eligibility. Deliberately NOT the
 * (accountId, archived, lastActiveAt) entry, which the manifest declares but no
 * code queries, so it may never have been built in the live project; a
 * FAILED_PRECONDITION there would resubscribe on backoff forever.
 *
 * Fail-soft in the direction that matters: an errored stream leaves the caller's
 * last set in place and resubscribes, because the cost of a missing snapshot is
 * a row that still looks open (today's behaviour) while the cost of throwing is
 * the whole rail.
 */
/**
 * One archived thread, as the closure surfaces need it.
 *
 * Deliberately NOT `ChatThread`: that type carries `archived` but none of the
 * ATTRIBUTION — `archivedBy`, `archivedByName`, the timestamp — because no
 * surface had ever needed to say who closed a thread. Widening `ChatThread` to
 * carry three fields for one consumer would put them on every thread record in
 * the app; reading them off the document here keeps them where they are used.
 */
export interface ArchivedThreadFacts {
  threadId: string;
  archived: true;
  archivedBy?: string;
  archivedByName?: string;
  atMs: number;
}

export function subscribeArchivedChatThreadIndex(
  uid: string,
  instanceId: string,
  accountId: string,
  cb: (rows: ArchivedThreadFacts[]) => void,
): Unsubscribe {
  assertCurrentUid(uid, 'subscribeArchivedChatThreadIndex');
  const colRef = Paths.domainCol(uid, THREAD_DOMAIN);
  const instanceFilter = instanceId === MAIN_SURFACE_INSTANCE_ID
    ? where('instanceId', 'in', LEGACY_MAIN_INSTANCE_IDS)
    : where('instanceId', '==', instanceId);
  const threadQuery = query(
    colRef,
    instanceFilter,
    where('accountId', '==', accountId),
    where('archived', '==', true),
    orderBy('lastActiveAt', 'desc'),
    limit(ARCHIVED_CLOSURE_WINDOW),
  );

  let disposed = false;
  let inner: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryDelayMs = 2000;
  const attach = () => {
    if (disposed) return;
    inner = onSnapshot(threadQuery, (snapshot) => {
      retryDelayMs = 2000;
      cb(snapshot.docs.map((docSnap) => {
        const data = (docSnap.data() || {}) as Record<string, unknown>;
        const by = typeof data.archivedBy === 'string' ? data.archivedBy.trim() : '';
        const byName = typeof data.archivedByName === 'string' ? data.archivedByName.trim() : '';
        return {
          threadId: docSnap.id,
          archived: true as const,
          atMs: readArchivedAtMs(data),
          ...(by ? { archivedBy: by } : {}),
          ...(byName ? { archivedByName: byName } : {}),
        };
      }));
    }, (err) => {
      // Loud, like both live listeners (M11, INCIDENT-20260818): an index
      // problem or a rules denial here is invisible from the surfaces — every
      // row simply keeps looking open — so it must not fail silently.
      console.error('[chatThreadIndexRepository] archived thread index subscription errored — resubscribing', {
        uid,
        instanceId,
        accountId,
        delayMs: retryDelayMs,
        error: err instanceof Error ? err.message : String(err),
      });
      try { inner?.(); } catch { /* already torn down */ }
      inner = null;
      if (disposed) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        attach();
      }, retryDelayMs);
      retryDelayMs = Math.min(60_000, Math.round(retryDelayMs * 2));
    });
  };
  attach();
  return () => {
    disposed = true;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    try { inner?.(); } catch { /* ignore */ }
    inner = null;
  };
}

export async function upsertChatThread(
  uid: string,
  instanceId: string,
  thread: ChatThread,
  options?: {
    /**
     * True when this thread was created locally this session and the server
     * has not yet acknowledged it. Creates stamp `archived:false` (the index
     * query needs the field to exist); updates OMIT the field so a merge can
     * never un-archive a thread archived elsewhere (BUG-THREAD-RESURRECTION).
     */
    isCreate?: boolean;
    /**
     * Carry the thread's section on an UPDATE too. Only the fallback for a
     * filing whose direct write found no record passes it
     * (writeChatThreadCategory); every other update leaves the section alone.
     */
    withCategory?: boolean;
  },
): Promise<void> {
  addUserScopedWriteGuard(uid, 'upsertChatThread');
  assertCurrentUid(uid, 'upsertChatThread');
  const ref = threadRef(uid, thread.id);
  const activeAccountId = getActiveWorkspaceAccount();
  // BIND-AT-BIRTH (INCIDENT-20260818): a create racing the workspace
  // resolution is PARKED with its full payload instead of written unbound;
  // later upserts for a parked id refresh the parked snapshot (the flush
  // carries the newest state). The resolution listener flushes parked
  // entries as full create-shaped, BOUND writes; pagehide is the last-chance
  // unbound flush. A signed-in thread doc can therefore no longer be born
  // unbound while the page lives, and never partial at all.
  const isCreate = options?.isCreate === true;
  if (!activeAccountId && (isCreate || deferredCreates.has(thread.id))) {
    deferredCreates.set(thread.id, {
      uid,
      instanceId,
      thread: { ...thread },
      // Capture the intended workspace NOW (see the map's doc). A refresh of
      // an already-parked entry keeps its original intent — the create's
      // context, not the refresh's.
      intendedAccountId:
        deferredCreates.get(thread.id)?.intendedAccountId
        // Shared-thread labeling (2026-08-21): an explicit binding on the
        // thread object (a surfaced group-thread stub carrying its row's
        // workspace) is the strongest intent of all.
        ?? thread.accountId
        ?? readConfirmedWorkspace(uid)?.accountId
        ?? null,
    });
    return;
  }
  if (deferredCreates.has(thread.id)) {
    // Resolution landed and the caller upserted before the listener ran:
    // this very write completes the parked create.
    deferredCreates.delete(thread.id);
    options = { ...(options || {}), isCreate: true };
  }
  const data = toChatThreadDoc(thread, instanceId, {
    isCreate: options?.isCreate === true,
    withCategory: options?.withCategory === true,
    // Multi-org P2: stamp the immutable workspace binding on creates (the
    // publish point is the chat mount — services/activeWorkspace.ts).
    // Shared-thread labeling (2026-08-21): an EXPLICIT binding on the thread
    // object (a surfaced group-thread stub carrying its membership row's
    // workspace, or a repair re-create restoring a dropped thread's true
    // home) wins over the viewer's active workspace — a thread belongs to
    // ITS org, not whichever workspace happens to be open.
    accountId: thread.accountId || activeAccountId,
  });

  await enqueueUserWrite(uid, async () => {
    await withBackoff(() => debouncedSetDoc(ref, data, {
      debounceKey: ref.path,
      debounceMs: WRITE_DEBOUNCE_MS,
    }));
  });
}

/**
 * Cheap existence probe for a thread's index doc (2026-07-31 multi-device
 * directive). The recipient-side surfacing flow used to treat "not in the
 * local top-40 working set" as "no index doc" and re-stub the thread — a
 * merge write that clobbered the real doc's title/messageCount/lastActiveAt
 * on every reload and tab focus. A doc that exists (even outside the window)
 * must never be re-stubbed; it simply lives outside the window until real
 * activity returns it.
 */
export async function chatThreadIndexDocExists(uid: string, threadId: string): Promise<boolean> {
  assertCurrentUid(uid, 'chatThreadIndexDocExists');
  const snap = await withBackoff(() => getDoc(threadRef(uid, threadId)));
  return snap.exists();
}

/**
 * Single-doc read of a thread's index doc, parsed through the shared mapper
 * (routine list parity, 2026-08-02). Powers switchThread's navigation
 * hydration: opening a routine whose thread sits OUTSIDE the capped listen
 * window needs the record on demand — the subscription can never deliver it.
 * Returns null when the doc does not exist; `archived` is carried through so
 * the caller decides how to treat archived docs (the hydrate path falls back
 * to a minimal record rather than resurrecting one).
 */
export async function getChatThread(uid: string, threadId: string): Promise<ChatThread | null> {
  assertCurrentUid(uid, 'getChatThread');
  const snap = await withBackoff(() => getDoc(threadRef(uid, threadId)));
  if (!snap.exists()) return null;
  return fromChatThreadDoc(snap.data() as ChatThreadDoc);
}

/**
 * True when the thread's server doc exists and is archived. Used to confirm a
 * remote archive (thread vanished from another window's index snapshot) before
 * the local UI drops it — a thread can also leave the snapshot through
 * working-set churn, which must NOT close the user's active conversation.
 */
export async function isChatThreadArchivedOnServer(uid: string, threadId: string): Promise<boolean> {
  assertCurrentUid(uid, 'isChatThreadArchivedOnServer');
  const snap = await withBackoff(() => getDoc(threadRef(uid, threadId)));
  if (!snap.exists()) return false;
  const data = snap.data() as Partial<ChatThreadDoc>;
  return data.archived === true;
}

/**
 * The archive facts on this user's own index doc — `archived` plus the
 * attribution the shared-archive fanout stamps (2026-08-26: `archivedBy`,
 * `archivedByName`), so the remote-archive announcement can SAY who archived
 * the thread instead of letting the conversation silently vanish.
 */
export interface ChatThreadArchiveInfo {
  archived: boolean;
  archivedBy?: string;
  archivedByName?: string;
  /**
   * When it was archived, in ms — 0 when no usable stamp exists.
   *
   * Two writers, two field names, two types: the membership fanout writes
   * `archivedAt` as a number, threadJanitor writes `archivedAtTs` as a server
   * Timestamp, and the two client archive paths write neither. Both spellings
   * are resolved by `readArchivedAtMs`, and 0 means "say no date" rather than
   * "the epoch" — printing an unstamped archive as jan 1 1970 is the defect
   * that shape invites.
   */
  atMs: number;
  /**
   * Pinned threads wait for their pinner (catalog v18): 'approval' when the
   * archive completed because the people who pinned it accepted. The archiver
   * above is then the person who ASKED, and these name who accepted and whose
   * Accept completed it ('' when the last pass completed it with nobody left
   * to answer) — so the accepter's browsers stay quiet and the asker is told.
   */
  archivedVia?: 'approval';
  archivedApprovedByNames?: string[];
  archivedDecidedBy?: string;
  /** An open request to archive this thread, read from the same doc. */
  openRequest?: ThreadArchiveRequest;
}

export async function getChatThreadArchiveInfo(
  uid: string,
  threadId: string,
): Promise<ChatThreadArchiveInfo> {
  assertCurrentUid(uid, 'getChatThreadArchiveInfo');
  const snap = await withBackoff(() => getDoc(threadRef(uid, threadId)));
  if (!snap.exists()) return { archived: false, atMs: 0 };
  const data = snap.data() as Record<string, unknown>;
  return {
    archived: data.archived === true,
    atMs: readArchivedAtMs(data),
    ...(typeof data.archivedBy === 'string' && data.archivedBy.trim()
      ? { archivedBy: data.archivedBy.trim() } : {}),
    ...(typeof data.archivedByName === 'string' && data.archivedByName.trim()
      ? { archivedByName: data.archivedByName.trim() } : {}),
    ...(data.archivedVia === 'approval' ? { archivedVia: 'approval' as const } : {}),
    ...(Array.isArray(data.archivedApprovedByNames)
      ? { archivedApprovedByNames: data.archivedApprovedByNames.filter((n): n is string => typeof n === 'string' && n.trim() !== '').map((n) => n.trim()) }
      : {}),
    ...(typeof data.archivedDecidedBy === 'string' ? { archivedDecidedBy: data.archivedDecidedBy.trim() } : {}),
    ...(() => {
      const openRequest = parseArchiveRequestStamps(threadId, data);
      return openRequest ? { openRequest } : {};
    })(),
  };
}

export async function setActiveThread(
  uid: string,
  instanceId: string,
  threadId: string,
): Promise<void> {
  addUserScopedWriteGuard(uid, 'setActiveThread');
  assertCurrentUid(uid, 'setActiveThread');
  const ref = activeRef(uid);

  await enqueueUserWrite(uid, async () => {
    await withBackoff(() => debouncedSetDoc(ref, {
      [instanceId]: threadId,
    }, {
      debounceKey: ref.path,
      debounceMs: WRITE_DEBOUNCE_MS,
    }));
  });
}

export async function getActiveThread(uid: string, instanceId: string): Promise<string | null> {
  assertCurrentUid(uid, 'getActiveThread');
  const snap = await withBackoff(() => getDoc(activeRef(uid)));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  const value = data[instanceId];
  return typeof value === 'string' ? value : null;
}

export async function archiveChatThread(
  uid: string,
  instanceId: string,
  threadId: string,
): Promise<void> {
  addUserScopedWriteGuard(uid, 'archiveChatThread');
  assertCurrentUid(uid, 'archiveChatThread');
  // A create still PARKED (workspace unresolved) that gets archived must not
  // flush later as a fresh `archived:false` doc — the resolution listener and
  // the pagehide flush both write create-shaped payloads and would resurrect
  // the thread. Dropping the parked entry makes this archive the last word.
  deferredCreates.delete(threadId);
  const ref = threadRef(uid, threadId);

  await enqueueUserWrite(uid, async () => {
    // BUG-THREAD-RESURRECTION: the archive must NOT ride the shared
    // debounce. Upserts and archives both key on ref.path, and
    // DebounceManager.schedule cancels the pending task for its key — so
    // a debounced archive could be silently discarded by a racing upsert,
    // and a queued upsert flushing after the archive would re-stamp
    // archived:false. Cancel whatever is pending for this doc, then write
    // archived:true immediately. This runs inside the per-uid queue, so
    // an upsert enqueued ahead of the archive has already scheduled its
    // debounce by the time we run — and gets cancelled here.
    debounceManager.cancel(ref.path);
    await withBackoff(() => setDoc(ref, { archived: true, instanceId }, { merge: true }));
  });

  clearThreadSessionStorage(threadId);
  await archiveThreadSessionOnBackend(threadId).catch((error) => {
    // eslint-disable-next-line no-console
    console.warn('[chatThreadIndexRepository] Failed to archive backend thread session', { threadId, error });
  });
}

/**
 * ROUTINE HANDOFF ACK (Routine Threads, 2026-07-31): stamp
 * `routineHandoffAckAt` (ISO now) onto a routine thread's index doc after the
 * client auto-navigated to it — the one routine-mirror field the CLIENT owns.
 * The ack is the handoff effect's cross-device idempotence guard (a second
 * device's snapshot sees the ack and never re-navigates), so like
 * archiveChatThread it must NOT ride the shared debounce: a merge write, sent
 * immediately through the per-uid queue.
 */
export async function acknowledgeRoutineHandoff(uid: string, threadId: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'acknowledgeRoutineHandoff');
  assertCurrentUid(uid, 'acknowledgeRoutineHandoff');
  const ref = threadRef(uid, threadId);

  await enqueueUserWrite(uid, async () => {
    await withBackoff(() => setDoc(ref, { routineHandoffAckAt: new Date().toISOString() }, { merge: true }));
  });
}

/**
 * The page BELOW the live window — the threads `subscribeChatThreadIndex` can
 * never deliver because they fell out of the newest-40 by `lastActiveAt`.
 *
 * This function shipped in 2026 and was never called. As written it filtered
 * `instanceId` and ordered by `lastActiveAt` with NO `archived` clause, and
 * that query matches none of the declared `chat_threads` composites: an index
 * (instanceId, archived, lastActiveAt) cannot serve equality(instanceId) +
 * orderBy(lastActiveAt), because the skipped middle field breaks the prefix.
 * The first real call would have thrown FAILED_PRECONDITION inside withBackoff.
 *
 * So `archived == false` is the SEMANTIC fix and the INDEX fix at once: it
 * lands this query on exactly the composite the live listeners already run in
 * production — proven built, not merely declared (see the deliberate avoidance
 * of `accountId+archived+lastActiveAt` in subscribeArchivedChatThreadIndex).
 *
 * The instance filter mirrors the listeners' own: without the legacy `in`, the
 * main surface would page `site_chat` and skip every `global_chat` thread — the
 * same silent truncation this exists to end, one layer down.
 *
 * The cursor stays a DocumentSnapshot. A value cursor (startAfter(isoString))
 * skips every doc sharing that stamp, so a tie straddling a page boundary would
 * lose rows — in the function whose whole job is to stop losing rows.
 */
export async function loadOlderThreads(
  uid: string,
  instanceId: string,
  cursor: DocumentSnapshot | null,
  pageSize = DEFAULT_PAGE_SIZE,
  opts?: { accountId?: string | null },
): Promise<Page<ChatThread>> {
  assertCurrentUid(uid, 'loadOlderThreads');

  const colRef = Paths.domainCol(uid, THREAD_DOMAIN);
  const instanceFilter = instanceId === MAIN_SURFACE_INSTANCE_ID
    ? where('instanceId', 'in', LEGACY_MAIN_INSTANCE_IDS)
    : where('instanceId', '==', instanceId);
  const accountId = (opts?.accountId || '').trim();
  const pagingQuery = query(
    colRef,
    instanceFilter,
    // Riding (instanceId, accountId, archived, lastActiveAt) when scoped, and
    // (instanceId, archived, lastActiveAt) when not — both proven by the live
    // listeners above.
    ...(accountId ? [where('accountId', '==', accountId)] : []),
    where('archived', '==', false),
    orderBy('lastActiveAt', 'desc'),
    limit(pageSize),
    ...(cursor ? [startAfter(cursor)] : []),
  );

  const snap = await withBackoff(() => getDocs(pagingQuery));
  const items = snap.docs.map((docSnap) => fromChatThreadDoc(docSnap.data() as ChatThreadDoc));
  const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  return { items, nextCursor };
}

export function __resetPendingWritesByUidForTests(): void {
  pendingWritesByUid.clear();
}
